/**
 * This file is responsible for rendering the shipping and returns page in the application.
 *
 * The component uses server-side rendering (getServerSideProps) to fetch the page content, shipping data, bed shipping countries, bed shipping values, and translations for the page.
 * The page content, shipping data, bed shipping countries, and bed shipping values are fetched using the GetCmsPage and GetCmsBlocks GraphQL queries.
 *
 *
 *
 * The Help component from '@Hotels-at-Home/hah-fe-core' is used to render the shipping and returns content.
 *
 * If there's an error during server-side rendering or fetching the page content, shipping data, bed shipping countries, or bed shipping values, it is passed as a prop to the component and can be used to display an error message.
 *
 * The NextPage component from '~/ui/layouts/NextPage' is used to render the page layout.
 */

import React, { ReactElement } from 'react';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Help from '@Hotels-at-Home/hah-fe-core/dist/components/Help';
import { getLocalNavigationItems } from '../../../constants/helpNavigation';

import {
  publicQuery,
  GetCmsPage,
  GetCmsBlocks,
} from '@Hotels-at-Home/hah-fe-core/dist/services/graphql';
import { extractErrorMessage } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';
import apolloConfig from '~/constants/apolloConfig';
import { LanguageCode } from '@Hotels-at-Home/hah-fe-core/dist/constants';

import { TServerSideProps, IHelpContent } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { usePageViewEvent } from '@Hotels-at-Home/hah-fe-core/dist/hooks';

interface IShippingReturns {
  pageContent: IHelpContent;
  shippingData: IHelpContent;
  bedShippingCountries: IHelpContent;
  bedShippingValues: IHelpContent;
}

type TProps = TServerSideProps<IShippingReturns>;

const ShippingReturnsPage = (props: TProps): ReactElement => {
  const { error } = props;

  const { t } = useTranslation('', { keyPrefix: '' });

  usePageViewEvent();
  const meta = {
    title: `${t('$local.$meta.meta_title')} | ${t('$common.$labels.shippingAndReturns')}`,
    description: `${t('$local.$meta.meta_title')} | ${t('$common.$labels.shippingAndReturns')}`,
  };

  return (
    <NextPage error={error} meta={meta}>
      <Help {...props} getLocalNavigationItems={getLocalNavigationItems} />
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps<TProps> = async ctx => {
  const props: TProps = {
    ...(await serverSideTranslations(ctx.locale)),
    error: null,
    pageContent: null,
    shippingData: null,
    bedShippingCountries: null,
    bedShippingValues: null,
  };

  try {
    props.pageContent = await publicQuery(
      apolloConfig(ctx.locale as LanguageCode),
      {
        query: GetCmsPage,
        variables: {
          locale: ctx.locale,
          name: 'shipping-and-returns',
        },
      },
      'GetCmsPage',
      ctx,
    ).then(res => res.data.cmsPage);
  } catch (e) {
    props.error = extractErrorMessage(e);
  }
  try {
    props.shippingData = await publicQuery(
      apolloConfig(ctx.locale as LanguageCode),
      {
        query: GetCmsBlocks,
        variables: {
          locale: ctx.locale,
          identifiers: [`shipping-data-${ctx.req.cookies.currency?.toLocaleLowerCase()}`],
        },
      },
      'GetCmsBlocks',
      ctx,
    ).then(res => res.data.cmsBlocks.items[0]);
  } catch (e) {
    props.error = extractErrorMessage(e);
  }
  try {
    props.bedShippingCountries = await publicQuery(
      apolloConfig(ctx.locale as LanguageCode),
      {
        query: GetCmsBlocks,
        variables: {
          locale: ctx.locale,
          identifiers: ['bed-shipping-countries'],
        },
      },
      'GetCmsBlocks',
      ctx,
    ).then(res => res.data.cmsBlocks.items[0]);
  } catch (e) {
    props.error = extractErrorMessage(e);
  }
  try {
    props.bedShippingValues = await publicQuery(
      apolloConfig(ctx.locale as LanguageCode),
      {
        query: GetCmsBlocks,
        variables: {
          locale: ctx.locale,
          identifiers: [`bed-shipping-values-${ctx.req.cookies.currency?.toLocaleLowerCase()}`],
        },
      },
      'GetCmsBlocks',
      ctx,
    ).then(res => res.data.cmsBlocks.items[0]);
  } catch (e) {
    props.error = extractErrorMessage(e);
  }

  return { props };
};

export default ShippingReturnsPage;
